import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Components
import Navbar from './components/Navbar';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyPage from './pages/VerifyPage';
import AboutPage from './pages/AboutPage'; 
import TechnologyPage from './pages/TechnologyPage';


// Manufacturer Pages
import ManufacturerRegister from './pages/ManufacturerRegister';
import ManufacturerDashboard from './pages/ManufacturerDashboard';
import ProductManagement from './pages/ProductManagement';
import BatchUpload from './pages/BatchUpload';
import ScanAnalytics from './pages/ScanAnalytics';
import BillingDashboard from './pages/BillingDashboard';

// Animation keyframes
const globalStyles = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideInLeft {
    from {
      opacity: 0;
      transform: translateX(-50px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(50px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes scaleIn {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
  }

  .fade-in-up {
    animation: fadeInUp 0.8s ease-out forwards;
  }

  .slide-in-left {
    animation: slideInLeft 0.8s ease-out forwards;
  }

  .slide-in-right {
    animation: slideInRight 0.8s ease-out forwards;
  }

  .scale-in {
    animation: scaleIn 0.6s ease-out forwards;
  }

  .hover-lift {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .hover-lift:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }

  .gradient-text {
    background: linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #06b6d4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .glass-effect {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.2);
  }

  .smooth-transition {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
`;

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
      }}>
        <div className="glass-effect" style={{
          padding: '60px',
          borderRadius: '24px',
          textAlign: 'center',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid #e2e8f0',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 24px'
          }}></div>
          <h3 style={{ 
            color: '#1e293b', 
            margin: '0 0 8px 0',
            fontSize: '18px',
            fontWeight: '600'
          }}>
            Loading ScanOnce
          </h3>
          <p style={{ 
            color: '#64748b', 
            margin: 0,
            fontSize: '14px'
          }}>
            Please wait while we prepare your dashboard
          </p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

// Manufacturer Protected Route
const ManufacturerRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  const isManufacturer = user?.role === 'manufacturer';
  
  return isAuthenticated && isManufacturer ? <>{children}</> : <Navigate to="/manufacturer/register" />;
};

// Dashboard Page Component
const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  
  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      padding: '40px 20px'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div className="glass-effect hover-lift fade-in-up" style={{
          padding: '60px',
          borderRadius: '24px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          textAlign: 'center'
        }}>
          <h1 className="gradient-text" style={{ 
            fontSize: '3rem',
            marginBottom: '24px', 
            fontWeight: '700',
            lineHeight: '1.2'
          }}>
            Welcome to ScanOnce 2.0
          </h1>
          <p style={{ 
            color: '#64748b', 
            fontSize: '20px',
            marginBottom: '40px',
            maxWidth: '600px',
            margin: '0 auto 40px',
            lineHeight: '1.6'
          }}>
            Hello {user?.firstName}, your universal product verification platform is ready.
          </p>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px',
            marginTop: '40px'
          }}>
            <a href="/verify" className="hover-lift smooth-transition" style={{
              display: 'block',
              padding: '32px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
              borderRadius: '16px',
              textDecoration: 'none',
              color: 'white',
              boxShadow: '0 10px 30px rgba(59, 130, 246, 0.3)'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '20px', fontWeight: '600' }}>
                Verify Products
              </h3>
              <p style={{ margin: 0, opacity: 0.9, fontSize: '14px', lineHeight: '1.5' }}>
                Scan barcodes and verify product authenticity across all industries
              </p>
            </a>
            
            <a href="/manufacturer/register" className="hover-lift smooth-transition" style={{
              display: 'block',
              padding: '32px',
              background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
              borderRadius: '16px',
              textDecoration: 'none',
              color: 'white',
              boxShadow: '0 10px 30px rgba(6, 182, 212, 0.3)'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏭</div>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '20px', fontWeight: '600' }}>
                For Manufacturers
              </h3>
              <p style={{ margin: 0, opacity: 0.9, fontSize: '14px', lineHeight: '1.5' }}>
                Register your company and start protecting your products today
              </p>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main App Content Component
const AppContent: React.FC = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  // Professional Hero Section Component
  const HeroSection: React.FC = () => {
    return (
      <section style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center'
      }}>
        {/* Background Pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `radial-gradient(circle at 25% 25%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
                           radial-gradient(circle at 75% 75%, rgba(6, 182, 212, 0.1) 0%, transparent 50%)`,
          animation: 'pulse 4s ease-in-out infinite'
        }}></div>

        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 20px',
          position: 'relative',
          zIndex: 1
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
            gap: '60px',
            alignItems: 'center'
          }}>
            {/* Left Content */}
            <div className={isLoaded ? 'slide-in-left' : ''} style={{
              opacity: isLoaded ? 1 : 0
            }}>
              <div style={{
                display: 'inline-block',
                padding: '8px 20px',
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '50px',
                marginBottom: '32px'
              }}>
                <span style={{
                  color: '#3b82f6',
                  fontSize: '14px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Enterprise Product Authentication
                </span>
              </div>

              <h1 style={{
                fontSize: '4rem',
                fontWeight: '800',
                color: 'white',
                marginBottom: '24px',
                lineHeight: '1.1',
                letterSpacing: '-0.02em'
              }}>
                Secure Your Products with{' '}
                <span className="gradient-text">
                  ScanOnce 2.0
                </span>
              </h1>

              <p style={{
                fontSize: '20px',
                color: '#94a3b8',
                marginBottom: '40px',
                lineHeight: '1.6',
                maxWidth: '500px'
              }}>
                Universal product verification platform that eliminates counterfeiting 
                through advanced cryptographic authentication. Trusted by manufacturers 
                across Kenya.
              </p>

              <div style={{
                display: 'flex',
                gap: '16px',
                flexWrap: 'wrap'
              }}>
                <a
                  href="/verify"
                  className="hover-lift smooth-transition"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '16px 32px',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                    color: 'white',
                    textDecoration: 'none',
                    borderRadius: '12px',
                    fontWeight: '600',
                    fontSize: '16px',
                    boxShadow: '0 10px 30px rgba(59, 130, 246, 0.4)'
                  }}
                >
                  Start Verification
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </a>

                <a
                  href="/manufacturer/register"
                  className="hover-lift smooth-transition"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '16px 32px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    textDecoration: 'none',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    fontWeight: '600',
                    fontSize: '16px',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  For Manufacturers
                </a>
              </div>

              {/* Trust Indicators */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '32px',
                marginTop: '48px',
                paddingTop: '32px',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <div>
                  <div style={{ color: 'white', fontSize: '24px', fontWeight: '700' }}>
                    99.9%
                  </div>
                  <div style={{ color: '#94a3b8', fontSize: '14px' }}>
                    Accuracy Rate
                  </div>
                </div>
                <div>
                  <div style={{ color: 'white', fontSize: '24px', fontWeight: '700' }}>
                    50ms
                  </div>
                  <div style={{ color: '#94a3b8', fontSize: '14px' }}>
                    Verification Speed
                  </div>
                </div>
                <div>
                  <div style={{ color: 'white', fontSize: '24px', fontWeight: '700' }}>
                    24/7
                  </div>
                  <div style={{ color: '#94a3b8', fontSize: '14px' }}>
                    Support Available
                  </div>
                </div>
              </div>
            </div>

            {/* Right Content - Interactive Demo */}
            <div className={isLoaded ? 'slide-in-right' : ''} style={{
              opacity: isLoaded ? 1 : 0
            }}>
              <div className="glass-effect hover-lift" style={{
                padding: '40px',
                borderRadius: '24px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '24px'
                }}>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: '#ef4444'
                  }}></div>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: '#f59e0b'
                  }}></div>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: '#22c55e'
                  }}></div>
                  <span style={{
                    marginLeft: '16px',
                    color: '#64748b',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    scanonce.co.ke/verify
                  </span>
                </div>

                <div style={{
                  background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                  borderRadius: '16px',
                  padding: '32px',
                  textAlign: 'center'
                }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                    borderRadius: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 24px',
                    animation: 'pulse 2s ease-in-out infinite'
                  }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <path d="M9 12l2 2 4-4"/>
                      <circle cx="12" cy="12" r="9"/>
                    </svg>
                  </div>

                  <h3 style={{
                    color: '#1e293b',
                    fontSize: '20px',
                    fontWeight: '600',
                    marginBottom: '12px'
                  }}>
                    Product Verified
                  </h3>

                  <p style={{
                    color: '#64748b',
                    fontSize: '14px',
                    marginBottom: '24px'
                  }}>
                    Authentic product confirmed in 47ms
                  </p>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px',
                    background: 'white',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ color: '#1e293b', fontSize: '14px', fontWeight: '600' }}>
                        Coca-Cola 500ml
                      </div>
                      <div style={{ color: '#64748b', fontSize: '12px' }}>
                        Batch: CC240315
                      </div>
                    </div>
                    <div style={{
                      padding: '4px 12px',
                      background: '#dcfce7',
                      color: '#166534',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      AUTHENTIC
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  };

  // Features Section
  const FeaturesSection: React.FC = () => {
    const features = [
      {
        title: "Universal Verification",
        description: "Works with any product across all industries using existing barcodes and batch codes",
        icon: "🌐"
      },
      {
        title: "Real-time Analytics",
        description: "Monitor scan patterns, detect counterfeits, and track product performance instantly",
        icon: "📊"
      },
      {
        title: "Enterprise Security",
        description: "Military-grade cryptographic protection that makes counterfeiting mathematically impossible",
        icon: "🔒"
      },
      {
        title: "Seamless Integration",
        description: "No production line changes required. Upload batch data and start protecting immediately",
        icon: "⚡"
      }
    ];

    return (
      <section style={{
        padding: '120px 20px',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <h2 className="gradient-text fade-in-up" style={{
              fontSize: '3rem',
              fontWeight: '700',
              marginBottom: '24px'
            }}>
              Why Choose ScanOnce 2.0
            </h2>
            <p className="fade-in-up" style={{
              fontSize: '20px',
              color: '#64748b',
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              Advanced product authentication technology trusted by manufacturers 
              across Kenya and beyond
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px'
          }}>
            {features.map((feature, index) => (
              <div
                key={index}
                className="glass-effect hover-lift scale-in"
                style={{
                  padding: '40px',
                  borderRadius: '20px',
                  textAlign: 'center',
                  animationDelay: `${index * 0.2}s`
                }}
              >
                <div style={{
                  fontSize: '48px',
                  marginBottom: '24px'
                }}>
                  {feature.icon}
                </div>
                <h3 style={{
                  color: '#1e293b',
                  fontSize: '20px',
                  fontWeight: '600',
                  marginBottom: '16px'
                }}>
                  {feature.title}
                </h3>
                <p style={{
                  color: '#64748b',
                  fontSize: '16px',
                  lineHeight: '1.6'
                }}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // HomePage Component
  const HomePage: React.FC = () => {
    return (
      <div>
        <style>{globalStyles}</style>
        <HeroSection />
        <FeaturesSection />
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh' }}>
      <style>{globalStyles}</style>
      <Navbar />
      <main>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verify" element={<VerifyPage />} />
          <Route path="/verify/:publicToken" element={<VerifyPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/technology" element={<TechnologyPage />} />
         

          {/* Manufacturer Routes */}
          <Route path="/manufacturer/register" element={<ManufacturerRegister />} />
          <Route 
            path="/manufacturer/dashboard" 
            element={
              <ManufacturerRoute>
                <ManufacturerDashboard />
              </ManufacturerRoute>
            } 
          />
          <Route 
            path="/manufacturer/products" 
            element={
              <ManufacturerRoute>
                <ProductManagement />
              </ManufacturerRoute>
            } 
          />
          <Route 
            path="/manufacturer/batches" 
            element={
              <ManufacturerRoute>
                <BatchUpload />
              </ManufacturerRoute>
            } 
          />
          <Route 
            path="/manufacturer/analytics" 
            element={
              <ManufacturerRoute>
                <ScanAnalytics />
              </ManufacturerRoute>
            } 
          />
          <Route 
            path="/manufacturer/billing" 
            element={
              <ManufacturerRoute>
                <BillingDashboard />
              </ManufacturerRoute>
            } 
          />

          {/* Consumer Dashboard */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
    </div>
  );
};

// Main App Component
const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
};

export default App;
